import { inferRouteObject } from 'react-router-typed-object';

import { generatePath, Navigate } from '@/js/libs/router';
import { and, not, or, PrivateRoute, Roles } from '@/js/libs/use-auth';

import type { Condition } from 'src/components/RoleBasedRedirector.tsx';
import RoleBasedRedirector from 'src/components/RoleBasedRedirector.tsx';
import CompanySettings from 'src/scenes/CompanySettings/index.ts';
import Status from 'src/scenes/Status/index.ts';
import { companySettingsRouteRoles } from './sectionRoles.ts';

const index = true;

const companySettingsConditions: Condition[] = [
  { roles: Roles.POSTADMANAGER, to: generatePath('/company-settings/settings/pdf-type-config') },
  { roles: not(Roles.POSTADMANAGER), to: generatePath('/company-settings/settings/emails') },
];

const companySettingsSectionConditions: Condition[] = [
  { roles: Roles.PAYOUT, to: './websites' },
  { roles: or(Roles.BOOK_CAMPAIGNS, Roles.POSTADMANAGER), to: './settings' },
  { roles: Roles.MANAGE_CAMPAIGN_TYPES, to: './campaign-types' },
  { roles: Roles.MANAGE_ACCOUNTS, to: './industries' },
  { roles: Roles.ADMIN, to: './brands' },
  { roles: and(Roles.REPORT_BUILDER, or(Roles.BOOK_CAMPAIGNS, Roles.SALESFUNNEL, Roles.POSTADMANAGER)), to: './labels' },
];

export const companySettingsRouteObject = inferRouteObject({
  path: 'company-settings',
  element: <PrivateRoute roles={companySettingsRouteRoles} noAccessComponent={Status.NotFound} />,
  children: [
    {
      Component: CompanySettings.Helmet,
      children: [
        {
          index,
          element: <RoleBasedRedirector conditions={companySettingsSectionConditions} noAccessElement={<Status.NotFound />} />,
        },
        {
          element: <PrivateRoute roles={Roles.BOOK_CAMPAIGNS} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'sales-units',
              Component: CompanySettings.SalesUnits,
            },
          ],
        },
        {
          element: <PrivateRoute roles={Roles.PAYOUT} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'websites',
              children: [
                { index, Component: CompanySettings.Websites.WebsitesList },
                { path: 'create', Component: CompanySettings.Websites.WebsiteForm },
                {
                  path: ':websiteId',
                  children: [
                    { index, Component: CompanySettings.Websites.WebsiteForm },
                    { path: 'history', Component: CompanySettings.Websites.WebsiteHistory },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'settings',
          element: <PrivateRoute roles={or(Roles.BOOK_CAMPAIGNS, Roles.POSTADMANAGER)} noAccessComponent={Status.NotFound} />,
          children: [
            {
              index,
              element: <RoleBasedRedirector conditions={companySettingsConditions} noAccessElement={<Status.NotFound />} />,
            },
            {
              Component: CompanySettings.CompanySettings.SalesUnitsProvider,
              children: [
                // Routes accessible only to non-POSTADMANAGER users
                {
                  element: <PrivateRoute roles={not(Roles.POSTADMANAGER)} noAccessComponent={Status.NotFound} />,
                  children: [
                    // Pages inside CompanySettings.Layout
                    {
                      Component: CompanySettings.CompanySettings.Layout,
                      children: [
                        {
                          path: 'pdf-footer',
                          Component: CompanySettings.CompanySettings.PDFFooterTemplates,
                        },
                        {
                          path: 'payment-details',
                          element: <PrivateRoute roles={Roles.ADMIN_INVOICE} noAccessComponent={Status.NotFound} />,
                          children: [{ index, Component: CompanySettings.CompanySettings.PaymentDetails }],
                        },
                        {
                          path: 'ppt-report-config',
                          Component: CompanySettings.CompanySettings.PPTReportConfig,
                        },
                      ],
                    },
                    // Pages not in CompanySettings.Layout
                    {
                      path: 'pdf-styles',
                      children: [
                        {
                          index,
                          Component: CompanySettings.CompanySettings.PDFStyles,
                        },
                        {
                          path: 'edit',
                          Component: CompanySettings.CompanySettings.PDFStylesForm,
                          searchParams: CompanySettings.CompanySettings.PDFStylesFormSearchParams.parse,
                        },
                      ],
                    },
                    {
                      path: 'emails',
                      children: [
                        {
                          index,
                          Component: CompanySettings.CompanySettings.EmailTemplates,
                        },
                        {
                          path: 'create',
                          Component: CompanySettings.CompanySettings.EmailTemplateForm,
                          searchParams: CompanySettings.CompanySettings.EmailTemplateFormSearchParams.parse,
                        },
                        {
                          path: ':emailTemplateId',
                          Component: CompanySettings.CompanySettings.EmailTemplateForm,
                        },
                      ],
                    },
                    {
                      path: 'email-designs',
                      children: [
                        {
                          index,
                          Component: CompanySettings.CompanySettings.EmailDesigns,
                        },
                        {
                          path: 'create',
                          Component: CompanySettings.CompanySettings.EmailDesignForm,
                          searchParams: CompanySettings.CompanySettings.EmailDesignFormSearchParams.parse,
                        },
                        {
                          path: ':emailDesignId',
                          Component: CompanySettings.CompanySettings.EmailDesignForm,
                          searchParams: CompanySettings.CompanySettings.EmailDesignFormSearchParams.parse,
                        },
                      ],
                    },
                    {
                      path: 'payment-details',
                      element: <PrivateRoute roles={Roles.ADMIN_INVOICE} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          path: 'create',
                          children: [
                            { index, element: <Navigate to='../' replace /> },
                            { path: ':salesUnitId', Component: CompanySettings.CompanySettings.PaymentDetailsForm },
                          ],
                        },
                        {
                          path: 'edit',
                          children: [
                            { index, element: <Navigate to='../' replace /> },
                            { path: ':invoiceInfoId', Component: CompanySettings.CompanySettings.PaymentDetailsForm },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'sequencing',
                      Component: CompanySettings.CompanySettings.Sequencing,
                    },
                    {
                      path: 'coop-weeks',
                      element: <PrivateRoute roles={Roles.MANAGE_COOP_WEEK} noAccessComponent={Status.NotFound} />,
                      children: [{ index, Component: CompanySettings.CompanySettings.CoopWeeks }],
                    },
                    {
                      path: 'user-id-modules',
                      element: <PrivateRoute roles={or(Roles.ADMIN, Roles.HEADER_TAG)} noAccessComponent={Status.NotFound} />,
                      children: [{ index, Component: CompanySettings.CompanySettings.UserIdModules }],
                    },
                  ],
                },
                // Routes accessible to all (including POSTADMANAGER)
                {
                  path: 'pdf-type-config',
                  Component: CompanySettings.CompanySettings.PDFTypeConfigurations,
                },
              ],
            },
          ],
        },
        {
          path: 'passendo',
          element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: CompanySettings.PassendoProgrammatic.Layout,

              children: [
                { path: 'websites-mapping', Component: CompanySettings.PassendoProgrammatic.WebsitesMapping },
                { index, element: <Navigate to='./websites-mapping' replace /> },
              ],
            },
          ],
        },
        {
          path: 'brands',
          element: <PrivateRoute roles={or(Roles.ADMIN, Roles.PAYOUT)} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: CompanySettings.Brands.Layout,
              children: [
                { index, Component: CompanySettings.Brands.List },
                { path: 'websites', Component: CompanySettings.Brands.Websites },
              ],
            },
          ],
        },
        {
          element: (
            <PrivateRoute
              roles={and(Roles.REPORT_BUILDER, or(Roles.BOOK_CAMPAIGNS, Roles.SALESFUNNEL, Roles.POSTADMANAGER))}
              noAccessComponent={Status.NotFound}
            />
          ),
          children: [{ path: 'labels', Component: CompanySettings.Labels }],
        },
        {
          element: <PrivateRoute roles={Roles.MANAGE_CAMPAIGN_TYPES} noAccessComponent={Status.NotFound} />,
          children: [
            { path: 'campaign-types', Component: CompanySettings.CampaignTypes },
            { path: 'campaign-types-v2', Component: CompanySettings.CampaignTypesV2 },
          ],
        },
        {
          element: <PrivateRoute roles={or(Roles.MANAGE_ACCOUNTS, Roles.POSTADMANAGER)} noAccessComponent={Status.NotFound} />,
          children: [{ path: 'industries', Component: CompanySettings.Industries }],
        },
        {
          element: <PrivateRoute roles={Roles.MANAGE_ACCOUNTS} noAccessComponent={Status.NotFound} />,
          children: [{ path: 'account-types', Component: CompanySettings.AccountTypes }],
        },
      ],
    },
  ],
});
